<template>
  <div class="position-relative">

    <!-- Search -->
    <b-card class="mt-4 shadow">
      <div class="mb-4" style="max-width: 450px;">
        <label class="font-weight-bold">
          Search by Email / Mobile / Name / Admission No:
        </label>
        <div class="input-group">
          <input
            v-model="searchKey"
            class="form-control"
            placeholder="Enter Email / Mobile / Name / Admission No"
            @keyup.enter="retrieveStudentLedger"
          />
          <span class="input-group-text bg-light" @click="retrieveStudentLedger">
            <i class="fas fa-search text-primary"></i>
          </span>
        </div>
      </div>
    </b-card>

    <!-- Error -->
    <div v-if="showNoLedger" class="alert alert-danger mt-3 text-center">
      No ledger found for the student.
    </div>

    <!-- Ledger -->
    <div v-if="hasTransactions">

      <div
        class="alert font-weight-bold text-center mb-4"
        :class="calculateDueAmount > 0 ? 'alert-danger' : 'alert-success'"
      >
        {{ calculateDueAmount > 0
          ? `DUE AMOUNT: ₹${calculateDueAmount}`
          : 'NO DUES PENDING ✅'
        }}
      </div>

      <!-- Student Info -->
      <div class="mb-4">
        <h5 class="border-bottom pb-2 mb-3 font-weight-bold">
          Student Information
        </h5>

        <div class="row">
          <div
            v-for="(label, key) in studentFields"
            :key="key"
            class="col-md-6 mb-2"
          >
            <strong>{{ label }}:</strong>
            <span>{{ getStudentFieldValue(key) }}</span>
          </div>
        </div>
      </div>

      <!-- Fee Table -->
      <div class="d-flex justify-content-between align-items-center border-bottom pb-2">
        <h5 class="font-weight-bold mb-0">
          Fee Details
          <small v-if="feeStructureName" class="text-muted font-weight-normal">({{ feeStructureName }})</small>
        </h5>
        <div class="d-flex align-items-center">
          <label class="mb-0 mr-3">
            <input type="checkbox" v-model="selectAllRows" :disabled="!payableRows.length" />
            Select All
          </label>
          <b-button variant="success" size="sm" v-if="totalBalance > 0" @click="openCollectFeeModal">
            Collect Fee
          </b-button>
        </div>
      </div>

      <div v-for="group in componentGroups" :key="group.component_id" class="mt-2">
        <h6 class="font-weight-bold mb-1" style="font-size: 14px !important;">{{ group.component }}</h6>
        <div class="table-responsive" style="height: auto !important; overflow-y: auto;">
          <table class="table table-sm table-bordered mb-0" style="table-layout: fixed;">
            <colgroup>
              <col style="width: 5%;" />
              <col style="width: 30%;" />
              <col style="width: 15%;" />
              <col style="width: 15%;" />
              <col style="width: 15%;" />
              <col style="width: 20%;" />
            </colgroup>
            <thead class="thead-light">
              <tr>
                <th></th>
                <th>Bill</th>
                <th class="text-right">Demand</th>
                <th class="text-right">Credit</th>
                <th class="text-right">Balance</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="row in group.rows" :key="row.group_id">
                <td>
                  <input
                    type="checkbox"
                    v-model="selectedGroups[row.group_id]"
                    :disabled="row.balance <= 0"
                  />
                </td>

                <td>
                  <template v-if="row.bill_name">
                    {{ row.bill_name }}
                    <small class="text-muted d-block">{{ row.bill_number }}</small>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>

                <td class="text-right">{{ formatAmount(row.amount) }}</td>

                <td class="text-right">{{ formatAmount(row.credit) }}</td>

                <td class="text-right">{{ formatAmount(row.balance) }}</td>

                <td>
                  <b-badge :variant="rowStatusVariant(row)">{{ rowStatus(row) }}</b-badge>
                </td>
              </tr>
            </tbody>

            <tfoot>
              <tr class="font-weight-bold" style="background-color: #e9ecef;">
                <td colspan="2">Total</td>
                <td class="text-right">{{ formatAmount(groupTotal(group, 'amount')) }}</td>
                <td class="text-right">{{ formatAmount(groupTotal(group, 'credit')) }}</td>
                <td class="text-right">{{ formatAmount(groupTotal(group, 'balance')) }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div class="alert alert-secondary d-flex justify-content-between font-weight-bold mt-4 mb-0">
        <span>Grand Total</span>
        <span>
          Total: {{ formatAmount(totalDebit) }}
          &nbsp;|&nbsp; Paid: {{ formatAmount(totalCredit) }}
          &nbsp;|&nbsp; Unpaid: {{ formatAmount(totalBalance) }}
        </span>
      </div>

      <!-- Payment History -->
      <div v-if="payments.length" class="mt-4">
        <h5 class="border-bottom pb-2 mb-3 font-weight-bold">Payment History</h5>
        <table class="table table-sm table-bordered">
          <thead class="thead-light">
            <tr>
              <th>Date</th>
              <th class="text-right">Amount Paid</th>
              <th>Payment Mode</th>
              <th>Collected By</th>
              <th>Receipt</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="payment in payments" :key="payment.id">
              <td>{{ formatDate(payment.collected_date) }}</td>
              <td class="text-right">{{ formatAmount(payment.fee_collected) }}</td>
              <td>{{ paymentModeText(payment.payment_mode) }}</td>
              <td>
                {{ payment.collected_by
                  ? `${payment.collected_by.first_name} ${payment.collected_by.last_name}`
                  : 'Online (Self-service)' }}
              </td>
              <td>
                <a href="#" class="small" @click.prevent="downloadReceipt(payment.id)">
                  <i class="fas fa-file-pdf mr-1"></i>Download
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Student List -->
    <div v-else-if="students.length">
      <h5 class="border-bottom pb-2 mb-3 font-weight-bold">Students</h5>

      <table class="table table-sm table-bordered">
        <thead>
          <tr>
            <th>Sl No</th>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Admission No</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(s, i) in students" :key="s.id">
            <td>{{ i + 1 }}</td>
            <td>{{ s.name }}</td>
            <td>{{ s.email }}</td>
            <td>{{ s.mobile }}</td>
            <td>{{ s.admission_no }}</td>
            <td>
              <b-button
                size="sm"
                variant="primary"
                @click="viewStudent(s)"
              >
                View
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Collect Fee Modal -->
    <b-modal
      v-model="showCollectFeeModal"
      title="Collect Fee"
      centered
      hide-footer
      @hidden="resetCollectFeeState"
    >
      <!-- Ordered collection: single amount, auto-applied top-to-bottom -->
      <div v-if="feeCollectionMode === 'ordered'" class="mb-3">
        <label><strong>Amount to Collect:</strong></label>
        <input
          type="number"
          class="form-control"
          min="0"
          :max="selectedBalance"
          v-model.number="orderedAmountInput"
        />
        <small class="text-danger" v-if="orderedAmountError">{{ orderedAmountError }}</small>

        <div class="mt-3" v-if="orderedComponentIds.length">
          <label class="d-block"><strong>This will be applied as:</strong></label>
          <ul class="list-unstyled mb-0">
            <li
              v-for="id in orderedComponentIds"
              :key="id"
              class="d-flex justify-content-between border-bottom py-1"
            >
              <span>{{ groupLabel(id) }}</span>
              <span>
                {{ formatAmount(collectedFees[id]) }}
                <small class="text-muted">(Remaining: {{ formatAmount(remainingBalance(id)) }})</small>
              </span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Manual collection: amount per component -->
      <div v-else class="mb-3">
        <label class="d-block"><strong>Enter amount for each component:</strong></label>
        <div v-for="id in orderedComponentIds" :key="id" class="mb-2">
          <label class="mb-1 d-block">
            {{ groupLabel(id) }}
            <small class="text-muted">(Balance: {{ formatAmount(componentBalance(id)) }})</small>
          </label>
          <ul v-if="groupBreakdown(id).length > 1" class="list-unstyled mb-1">
            <li
              v-for="(item, i) in groupBreakdown(id)"
              :key="i"
              class="d-flex justify-content-between"
              :class="item.isAdjustment ? (item.amount < 0 ? 'text-success' : 'text-danger') : 'text-muted'"
            >
              <small>{{ item.isAdjustment ? (item.note || 'Adjustment') : 'Base' }}</small>
              <small>{{ item.isAdjustment && item.amount > 0 ? '+' : '' }}{{ formatAmount(item.amount) }}</small>
            </li>
          </ul>
          <input
            type="number"
            class="form-control"
            min="0"
            :max="componentBalance(id)"
            v-model.number="collectedFees[id]"
            :disabled="!allowsPartial(id)"
          />
          <small v-if="!allowsPartial(id)" class="text-muted">
            Partial payment isn't allowed for this component - full amount only.
          </small>
          <small class="text-danger" v-if="collectedFeeErrors[id]">
            {{ collectedFeeErrors[id] }}
          </small>
        </div>
      </div>

      <span
        v-if="totalCollected > totalBalance"
        class="text-danger font-weight-bold d-block text-center mb-3"
      >
        Warning: Collected amount exceeds total balance.
      </span>

      <div class="mb-3">
        <label><strong>Collected Amount:</strong></label>
        <p class="text-success font-weight-bold bg-light p-2">
          ₹{{ totalCollected }}
        </p>
      </div>

      <div class="mb-3">
        <label><strong>Remaining Amount:</strong></label>
        <p>₹{{ totalRemaining }}</p>
      </div>

      <div class="mb-3">
        <label><strong>Payment Mode:</strong></label>
        <b-form-radio-group
          v-model="paymentMode"
          :options="paymentModeOptions"
        />
        <small class="text-danger d-block" v-if="exceedsFeeCredit">
          Amount is more than the Fee Credit balance.
        </small>
      </div>

      <div class="mb-3">
        <label><strong>Collected Date:</strong></label>
        <p>{{ formattedDate }}</p>
      </div>

      <div class="mb-3">
        <label><strong>Collected By:</strong></label>
        <p>{{ profile.username }}</p>
      </div>

      <div class="text-center">
        <b-button variant="outline-secondary" class="mr-2" @click="showCollectFeeModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" :disabled="!canCollect" @click="submitPayment">
          Submit
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
export default {
  data() {
    return {
      searchKey: "",
      students: [],
      transactionDatas: null,
      componentRows: [],
      selectedGroups: {},
      isTransactionNotFound: false,
      payments: [],
      walletBalance: 0,

      collectedFees: {},
      collectedFeeErrors: {},

      feeCollectionMode: "manual",
      orderedAmountInput: 0,
      orderedAmountError: "",

      showCollectFeeModal: false,
      paymentMode: "1",
      collectedDate: new Date(),

      paymentModes: [
        { value: "1", text: "Cash" },
        { value: "2", text: "Bank Transfer" },
        { value: "3", text: "Cheque" },
        { value: "4", text: "DD" },
        { value: "5", text: "POS" },
        { value: "6", text: "RTGS" },
        { value: "7", text: "NEFT" },
        { value: "8", text: "UPI" },
        { value: "9", text: "Bank Loan" },
        { value: "10", text: "Wallet" },
      ],

      studentFields: {
        full_name: "Full Name",
        admission_no: "Admission Number",
        fee_category: "Fee Structure",
        academic_info: "Academic Year",
        email: "Email",
        mobile: "Mobile Number",
      },
    };
  },

  computed: {
    profile() {
      return this.$store.getters.getUser;
    },

    formattedDate() {
      const d = new Date(this.collectedDate);
      return `${String(d.getDate()).padStart(2, "0")}/${
        String(d.getMonth() + 1).padStart(2, "0")
      }/${d.getFullYear()}`;
    },

    hasTransactions() {
      return Array.isArray(this.transactionDatas) && this.transactionDatas.length > 0;
    },

    feeStructureName() {
      if (!this.hasTransactions) return "";
      return this.transactionDatas[0].fee_student_category_mapping.feecategory.name;
    },

    showNoLedger() {
      return !this.hasTransactions && !this.students.length && this.isTransactionNotFound;
    },

    groupedTransactions() {
      if (!this.hasTransactions) return {};
      // Grouped by bill when one applies (matching the backend's component_rows),
      // so a payment against a bill-tagged charge nets into the same group
      // instead of a bill-less credit landing in a separate component-only bucket.
      // Keyed by fee_bill_student_mapping_id, not fee_bill_id - the latter now
      // identifies the shared bill event (same id for every student on it),
      // not this specific student's row, so it can no longer be used as the
      // per-student grouping key here.
      return this.transactionDatas.reduce((acc, tx) => {
        const id = tx.fee_bill_student_mapping_id ? `bill-${tx.fee_bill_student_mapping_id}` : `component-${tx.fee_component.id}`;
        acc[id] ??= [];
        acc[id].push(tx);
        return acc;
      }, {});
    },

    calculateDueAmount() {
      return (this.totalDebit - this.totalCredit).toFixed(2);
    },

    totalDebit() {
      return Object.values(this.groupedTransactions)
        .reduce((s, g) => s + this.getTotal(g, 'debit_amount'), 0);
    },

    totalCredit() {
      return Object.values(this.groupedTransactions)
        .reduce((s, g) => s + this.getTotal(g, "credit_amount"), 0);
    },

    totalBalance() {
      return this.totalDebit - this.totalCredit;
    },

    totalCollected() {
      return Object.values(this.collectedFees)
        .reduce((s, v) => s + (v || 0), 0);
    },

    totalRemaining() {
      return this.selectedBalance - this.totalCollected;
    },

    paymentModeOptions() {
      if (this.walletBalance <= 0) return this.paymentModes;
      return [
        ...this.paymentModes,
        { value: "12", text: `Fee Credit (${this.formatAmount(this.walletBalance)})` },
      ];
    },

    exceedsFeeCredit() {
      return this.paymentMode === "12" && this.totalCollected > this.walletBalance;
    },

    canCollect() {
      return this.totalCollected > 0
        && this.totalCollected <= this.selectedBalance
        && !this.exceedsFeeCredit;
    },

    // Rows a staff member could actually pick to pay - a zero-balance row
    // (fully settled, or nothing ever due) has nothing to collect.
    payableRows() {
      return this.componentRows.filter((row) => row.balance > 0);
    },

    // A component can have multiple bills (e.g. one per period/adjustment
    // round) - group its rows together so the Component column rowspans
    // over all of that component's bills instead of repeating per row.
    componentGroups() {
      const map = {};
      const order = [];
      this.componentRows.forEach((row) => {
        const key = row.fee_component_id;
        if (!map[key]) {
          map[key] = {
            component_id: key,
            component: row.component,
            category: row.category,
            rows: [],
          };
          order.push(key);
        }
        map[key].rows.push(row);
      });
      return order.map((key) => map[key]);
    },

    selectAllRows: {
      get() {
        return this.payableRows.length > 0
          && this.payableRows.every((row) => this.selectedGroups[row.group_id]);
      },
      set(value) {
        this.payableRows.forEach((row) => {
          this.$set(this.selectedGroups, row.group_id, value);
        });
      },
    },

    // Only the bills/components the staff explicitly selected belong in the
    // Collect Fee form - previously every open item was included by default.
    orderedComponentIds() {
      return Object.keys(this.groupedTransactions)
        .filter((id) => this.selectedGroups[id] && this.componentBalance(id) > 0)
        .sort((a, b) => {
          const orderA = this.groupedTransactions[a][0].fee_component.sort_order;
          const orderB = this.groupedTransactions[b][0].fee_component.sort_order;
          if (orderA == null && orderB == null) return 0;
          if (orderA == null) return 1;
          if (orderB == null) return -1;
          return orderA - orderB;
        });
    },

    selectedBalance() {
      return this.orderedComponentIds.reduce((s, id) => s + this.componentBalance(id), 0);
    },
  },

  watch: {
    collectedFees: {
      deep: true,
      handler() {
        Object.keys(this.groupedTransactions).forEach(id => {
          if (this.remainingBalance(id) < 0) {
            this.$set(
              this.collectedFeeErrors,
              id,
              "Collected fee cannot exceed remaining balance."
            );
          } else {
            this.$delete(this.collectedFeeErrors, id);
          }
        });
      }
    },

    orderedAmountInput() {
      this.applyOrderedAllocation();
    },
  },

  async created() {
    await this.loadCollectionMode();
  },

  methods: {
    async loadCollectionMode() {
      const url = `${this.$store.getters.getAPIKey.mainAPI}/institution/setting/fee_collection_mode`;
      const res = await this.$axios.get(url);
      this.feeCollectionMode = res.data || "manual";
    },

    applyOrderedAllocation() {
      let remaining = Number(this.orderedAmountInput) || 0;
      this.orderedAmountError = "";

      if (remaining > this.selectedBalance) {
        remaining = this.selectedBalance;
        this.orderedAmountError = "Amount cannot exceed the selected bills' balance; capped.";
      }

      const newCollectedFees = {};
      this.orderedComponentIds.forEach(id => {
        const balance = this.componentBalance(id);
        // A non-partial component is all-or-nothing: only apply once the
        // remaining amount can cover it in full, never a partial slice.
        const applied = this.allowsPartial(id)
          ? Math.min(balance, remaining)
          : (remaining >= balance ? balance : 0);
        newCollectedFees[id] = applied;
        remaining -= applied;
      });

      this.collectedFees = newCollectedFees;
    },

    formatDate(d) {
      if (!d) return "";
      const [y, m, day] = d.split("-");
      return `${day}/${m}/${y}`;
    },

    paymentModeText(mode) {
      // Deliberately separate from paymentModes (the staff-selectable radio
      // group in the Collect Fee modal) - a student may have paid online
      // themselves, which should show up in this history but is never a
      // mode staff should be able to pick manually.
      const modes = {
        1: "Cash", 2: "Bank Transfer", 3: "Cheque", 4: "DD", 5: "POS",
        6: "RTGS", 7: "NEFT", 8: "UPI", 9: "Bank Loan", 10: "Wallet", 11: "Online", 12: "Fee Credit",
      };
      return modes[mode] || mode;
    },

    async downloadReceipt(feeCollectionId) {
      const url = `${this.$store.getters.getAPIKey.mainAPI}/feecollections/${feeCollectionId}/receipt`;
      try {
        const response = await this.$axios.get(url, { responseType: "blob" });
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", `Receipt-${feeCollectionId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        this.$toast.error("Failed to download receipt. Please try again.");
      }
    },

    formatAmount(v) {
      return Number(v || 0).toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
      });
    },

    getTotal(arr, key) {
      return arr.reduce((s, t) => s + Number(t[key] || 0), 0);
    },

    groupTotal(group, key) {
      return group.rows.reduce((s, row) => s + Number(row[key] || 0), 0);
    },

    rowStatus(row) {
      if (row.balance <= 0.0001) return "Paid";
      if (row.credit > 0) return "Partially Paid";
      return "Unpaid";
    },

    rowStatusVariant(row) {
      const status = this.rowStatus(row);
      if (status === "Paid") return "success";
      if (status === "Partially Paid") return "warning";
      return "danger";
    },

    groupLabel(id) {
      const first = this.groupedTransactions[id][0];
      const name = first.fee_component ? first.fee_component.name : '';
      return first.fee_bill ? `${name} - ${first.fee_bill.name} (${first.fee_bill.bill_number})` : name;
    },

    groupBreakdown(id) {
      return this.groupedTransactions[id]
        .filter((tx) => Number(tx.debit_amount || 0) !== 0)
        .map((tx) => ({
          isAdjustment: !!tx.is_adjustment,
          amount: Number(tx.debit_amount || 0),
          note: tx.note,
        }));
    },

    componentBalance(id) {
      const g = this.groupedTransactions[id];
      return this.getTotal(g, 'debit_amount') - this.getTotal(g, "credit_amount");
    },

    remainingBalance(id) {
      return this.componentBalance(id) - (this.collectedFees[id] || 0);
    },

    allowsPartial(id) {
      const first = this.groupedTransactions[id][0];
      return !!(first.fee_component && first.fee_component.allow_partial_payment);
    },

    buildAllocations() {
      // Only the bills/components the staff selected this round get a
      // transaction - unselected ones are left completely untouched.
      return this.orderedComponentIds.map((id) => {
        const first = this.groupedTransactions[id][0];
        return {
          fee_component_id: first.fee_component.id,
          fee_bill_id: first.fee_bill_id || null,
          credit_amount: this.collectedFees[id] || 0,
          remaining_amount: this.remainingBalance(id),
        };
      });
    },

    getStudentFieldValue(field) {
      const map = this.transactionDatas[0].fee_student_category_mapping;
      const u = map.institution_user;
      const c = map.feecategory;

      return {
        full_name: `${u.first_name} ${u.last_name}`,
        admission_no: u.admission_no,
        fee_category: c.name,
        academic_info: `${c.academic_year} - ${c.academic_semester}`,
        email: u.email,
        mobile: u.mobile,
      }[field];
    },

    async viewStudent(student) {
      this.students = [];
      this.transactionDatas = null;
      this.componentRows = [];
      this.collectedFees = {};
      this.selectedGroups = {};
      this.payments = [];

      const url = `${this.$store.getters.getAPIKey.mainAPI}/studentcategorymappings/view-transaction`;
      const res = await this.$axios.get(url, {
        params: { institution_user_id: student.id },
      });

      this.transactionDatas = res.data.transactions || [];
      this.componentRows = res.data.component_rows || [];
      this.payments = res.data.payments || [];
      this.walletBalance = res.data.wallet_balance || 0;
      this.collectedFees = Object.keys(this.groupedTransactions)
        .reduce((a, k) => ({ ...a, [k]: 0 }), {});
      // Nothing pre-selected - staff picks which bill(s) they're collecting for.
      this.selectedGroups = this.componentRows
        .reduce((a, row) => ({ ...a, [row.group_id]: false }), {});
    },

    resetCollectFeeState() {
      // A component that doesn't allow partial payment is always paid in
      // full - pre-fill and lock it rather than leaving it at 0 for typing.
      // Scoped to the bills actually selected this round - looping over
      // every open bill here silently added unselected non-partial bills'
      // full balance into the total, even though they have no input row in
      // this modal at all.
      this.collectedFees = this.orderedComponentIds
        .reduce((a, k) => ({
          ...a,
          [k]: this.allowsPartial(k) ? 0 : this.componentBalance(k),
        }), {});
      this.orderedAmountInput = 0;
      this.orderedAmountError = "";
    },

    openCollectFeeModal() {
      if (!this.orderedComponentIds.length) {
        this.$toast.error("Please select at least one bill to collect payment for.");
        return;
      }
      this.resetCollectFeeState();
      this.showCollectFeeModal = true;
    },

    async submitPayment() {
      if (!this.paymentMode) {
        this.$toast.error("Please select payment mode");
        return;
      }

      const url = `${this.$store.getters.getAPIKey.mainAPI}/studentcategorymappings/collect-fee`;

      await this.$axios.post(url, {
        fee_ledger_id: this.transactionDatas[0].fee_ledger_id,
        studentCategoryMapId: this.transactionDatas[0].fee_student_category_mapping.id,
        allocations: this.buildAllocations(),
        payment_mode: this.paymentMode,
        fee_collected: this.totalCollected,
        balance_amount: this.totalRemaining,
      });

      this.$toast.success("Fee Collected");

      await this.viewStudent({
        id: this.transactionDatas[0].fee_student_category_mapping.institution_user.id
      });

      this.showCollectFeeModal = false;
      this.paymentMode = "1";
      this.collectedDate = new Date();
    },

    async retrieveStudentLedger() {
      this.isTransactionNotFound = false;
      this.students = [];
      this.transactionDatas = null;

      const url = `${this.$store.getters.getAPIKey.mainAPI}/studentcategorymappings/view-ledger/student-list`;
      const res = await this.$axios.get(url, {
        params: { search: this.searchKey },
      });

      this.students = res.data || [];
      if (!this.students.length) this.isTransactionNotFound = true;
    },
  },
};
</script>